import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, Tag, Typography } from 'antd';
import { FireFilled, DatabaseOutlined } from '@ant-design/icons';
import { searchColeccionItemsApi } from 'api/hallazgoFlujo';
import ColeccionItemImageThumb from 'components/forms/ColeccionItemImageThumb';

// Fase 4 del roadmap Hallazgos->Presupuesto->ODT.
// Autocomplete contra el banco de items del catalogo con ranking por uso.
// El backend (/coleccion-items/search) ordena items mas usados primero.
//
// Props:
//   value: id del item seleccionado (o null).
//   onChange(item): se llama con el objeto completo al seleccionar
//     (id, nombre, precio, moneda, tipo_item, usageCount).
//   onClear: llamado cuando el usuario limpia la selección.
//   moneda: filtro opcional — solo items de esa moneda.
//   tipo: filtro opcional — PRODUCTO/SERVICIO/OTRO.
//   size: 'small' | 'middle' | 'large'.
//   placeholder: override.
//
// Debounce 250ms, trigger con min 2 chars. Cache en memoria por query.

const MONEDA_SYMBOLS = { CLP: '$', UF: 'UF', USD: 'US$' };
const formatearPrecio = (precio, moneda) => {
  if (precio == null) return '';
  const decimals = moneda === 'CLP' ? 0 : 2;
  return `${MONEDA_SYMBOLS[moneda] || '$'} ${Number(precio).toLocaleString('es-CL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

function CatalogoItemPicker({
  value,
  onChange,
  onClear,
  moneda,
  tipo,
  size = 'middle',
  placeholder = 'Buscar en catálogo…',
  style,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [cache, setCache] = useState({});

  const cacheKey = useMemo(
    () => `${searchText}|${moneda || ''}|${tipo || ''}`,
    [searchText, moneda, tipo]
  );

  // Carga inicial sin query (top items por uso).
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoading(true);
      const res = await searchColeccionItemsApi({ moneda, tipo, limit: 20 });
      if (cancel) return;
      const items = Array.isArray(res) ? res : [];
      setOptions(items);
      setCache((c) => ({ ...c, [`|${moneda || ''}|${tipo || ''}`]: items }));
      setLoading(false);
    };
    load();
    return () => {
      cancel = true;
    };
  }, [moneda, tipo]);

  // Debounce del search.
  useEffect(() => {
    if (!searchText || searchText.length < 2) return;
    if (cache[cacheKey]) {
      setOptions(cache[cacheKey]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await searchColeccionItemsApi({
        q: searchText,
        moneda,
        tipo,
        limit: 20,
      });
      const items = Array.isArray(res) ? res : [];
      setOptions(items);
      setCache((c) => ({ ...c, [cacheKey]: items }));
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchText, cacheKey, cache, moneda, tipo]);

  const handleSelect = useCallback(
    (id) => {
      const item = options.find((o) => o.id === id);
      if (item && onChange) onChange(item);
    },
    [options, onChange]
  );

  const handleClear = useCallback(() => {
    if (onClear) onClear();
    else if (onChange) onChange(null);
  }, [onChange, onClear]);

  const renderOption = useCallback((item) => {
    const usageCount = Number(item.usageCount) || 0;
    const popular = usageCount >= 3;
    return {
      value: item.id,
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {popular ? (
              <FireFilled style={{ color: '#c5561d', fontSize: 12 }} />
            ) : (
              <DatabaseOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
            )}
            {item.image_key ? (
              <ColeccionItemImageThumb itemId={item.id} imageKey={item.image_key} size={24} />
            ) : null}
            <span
              style={{
                fontWeight: popular ? 600 : 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.nombre}
            </span>
            <Tag
              color={item.tipo_item === 'SERVICIO' ? 'blue' : 'default'}
              style={{ marginInlineEnd: 0, fontSize: 10 }}
            >
              {item.tipo_item}
            </Tag>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: '#475569',
            }}
          >
            {item.precio != null ? <span>{formatearPrecio(item.precio, item.moneda)}</span> : null}
            {usageCount > 0 ? (
              <span
                style={{
                  fontSize: 10,
                  color: popular ? '#c5561d' : '#94a3b8',
                }}
                title={`Usado ${usageCount} veces`}
              >
                × {usageCount}
              </span>
            ) : null}
          </div>
        </div>
      ),
      data: item,
    };
  }, []);

  const rendered = options.map(renderOption);

  return (
    <Select
      showSearch
      allowClear
      value={value}
      onChange={handleSelect}
      onClear={handleClear}
      onSearch={setSearchText}
      placeholder={placeholder}
      size={size}
      style={style}
      loading={loading}
      filterOption={false}
      options={rendered}
      notFoundContent={
        loading ? (
          'Buscando…'
        ) : searchText.length < 2 && options.length === 0 ? (
          'Escribe al menos 2 caracteres'
        ) : (
          <Typography.Text type="secondary">Sin resultados</Typography.Text>
        )
      }
    />
  );
}

export default CatalogoItemPicker;
